import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { Notice } from "components/common-ui";
import LinkUpStore from "../link-up.store";

type EnterOtpProps = {
    display: boolean,
    cancellationToken: any,
    errorMessage?: string,
    onOtpChange: any,
    onValidate: any,
    store: LinkUpStore,
    onResend: any,
    onCancel: any
}
const codeRegex = /^\d{4}$/;
const keysThatIgnoreMove = ["Backspace", "Delete"];
const keysToExclude = [...keysThatIgnoreMove, "ControlLeft", "ControlRight", "KeyV"];

const isKeyValid = (e: any, code: string) => {
    if (!code.startsWith("Digit") &&
        !code.includes("Numpad") &&
        !code.includes("Arrow") &&
        !keysToExclude.includes(code)) {
        e.preventDefault();
        return false;
    }

    return true;
}

const keyCode = {
    arrowLeft: "ArrowLeft",
    backSpace: "Backspace",
    delete: "Delete",
    keyV: "KeyV",
    controlLeft: "ControlLeft"
}

const keyMapping = {
    8: keyCode.backSpace,
    17: keyCode.controlLeft,
    46: keyCode.delete,
    86: keyCode.keyV,

    37: "ArrowLeft",
    39: "ArrowRight",

    48: "Digit0",
    49: "Digit1",
    50: "Digit2",
    51: "Digit3",
    52: "Digit4",
    53: "Digit5",
    54: "Digit6",
    55: "Digit7",
    56: "Digit8",
    57: "Digit9",

    96: "Numpad0",
    97: "Numpad1",
    98: "Numpad2",
    99: "Numpad3",
    100: "Numpad4",
    101: "Numpad5",
    102: "Numpad6",
    103: "Numpad7",
    104: "Numpad8",
    105: "Numpad9"
};

const mapKey = (e: any) => {
    if (e.code) {
        return e.code;
    }

    //Note: Android doesn't support e.code so need to map
    if (!e.keyCode) {
        return null;
    }

    const key = e.keyCode as keyof typeof keyMapping;
    const mapped = keyMapping[key];

    return mapped;
}

const EnterOtp = observer((props: EnterOtpProps) => {
    const {
        display,
        cancellationToken,
        errorMessage,
        store,
        onOtpChange,
        onValidate,
        onResend,
        onCancel
    } = props;
    const keys = [1, 2, 3, 4];
    const [isValidating, setIsValidating] = useState(false);
    const [isResending, setIsResending] = useState(false);

    const className = "enter-otp";
    const errorClassName = "error";
    const fieldSetClassName = errorMessage == null ? "" : errorClassName;

    const sentTo = store.sendOtpTo;

    const verifyChar = (e: any) => isKeyValid(e, mapKey(e));

    const onPaste = (e: any) => {
        const pastedValue = e.clipboardData.getData("Text");

        if (!codeRegex.test(pastedValue)) {
            return;
        }

        for (let i = 1; i <= 4; i++) {
            getInput(i).value = pastedValue[(i - 1)];
        }

        onChange();
    }

    const getInput = (i: number) => {
        return document.getElementById("otp-" + i) as HTMLInputElement;
    }

    const onChange = () => {
        const otp = keys.map(i => getInput(i).value).join("");

        const fieldSet = document.querySelector(`.${className}`)?.querySelector("fieldset") as HTMLFieldSetElement;
        fieldSet.classList.remove(errorClassName);

        if (onOtpChange != null) {
            onOtpChange();
        }

        store.updateOtpValue(otp);
    }

    //0-9 (48-57) numpad 0-9 (96-105)
    const moveCursor = (e: any) => {
        const code = mapKey(e);

        if (!isKeyValid(e, code)) {
            return;
        }

        if (code === "Delete") {
            return;
        }

        const index = parseInt(e.target.id.split("-")[1]);

        const goBackKeys = [keyCode.backSpace, keyCode.arrowLeft];

        const isBackSpace = goBackKeys.includes(code);

        const noActionIndex = isBackSpace ? 1 : 4;

        if (index === noActionIndex) {
            return;
        }

        const newIndex = isBackSpace ? index - 1 : index + 1;

        getInput(newIndex).focus();
    }

    const validate = async () => {
        setIsValidating(true);

        await onValidate(store.otpValue);

        setIsValidating(false);
    }

    const resend = async (e: any) => {
        if (e != null) {
            e.preventDefault();
        }

        if (isValidating) {
            return;
        }

        cancellationToken.cancel();

        setIsResending(true);

        for (let i = 1; i <= 4; i++) {
            getInput(i).value = "";
        }

        onChange();

        await onResend();

        setIsResending(false);
    }

    const cancel = (e: any) => {
        if (e != null) {
            e.preventDefault();
        }

        cancellationToken.cancel();

        setIsResending(false);

        setIsValidating(false);

        onCancel();
    }

    if (display !== true) {
        return null;
    }

    const message = `Please enter the 4 digit code sent to ${sentTo?.maskedValue}`;

    return (
        <Notice className={className}>
            <h1>We've sent you a code</h1>

            <p dangerouslySetInnerHTML={{ __html: message }} />

            {/* https://web.dev/sms-otp-form/ */}

            <fieldset className={fieldSetClassName}>
                {keys?.map(i =>
                    <input
                        id={`otp-${i}`}
                        key={i}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d{1}"
                        required
                        type="text"
                        onKeyDown={verifyChar}
                        onPaste={onPaste}
                        onKeyUp={moveCursor}
                        onChange={onChange}
                        size={1}
                        maxLength={1} />
                )}
            </fieldset>

            <p className="error-message">
                {errorMessage}
            </p>

            {isValidating
                ? <p className="processing">Validating OTP</p>
                : isResending
                    ? <p className="processing">Resending OTP</p>
                    : <button className={`btn btn-black${store.otpValue.length < 4 ? " disabled" : ""}`}
                        onClick={validate}>Continue</button>}

            <span className="anchor action" onClick={cancel}>Cancel</span>

            {/* //What does cancel do?
            //Indicate otp lasts for so long? */}

            <hr />

            {store.showResentMessage
                ? <p className="re-sent">
                    <i className="icon-tick icon-va" />
                    Code has been resent to {store.sendOtpTo?.maskedValue}
                </p>
                : <p>
                    Didn't get a code?&nbsp;
                    <span className="anchor" onClick={resend}>Resend</span>
                </p>}
        </Notice>
    );
});

export default EnterOtp;
